import { useState } from 'react';
import { api } from '../../lib/api';
import type { Customer } from '../../lib/types';
import { SearchSelect } from '../../components/SearchSelect';
import { Field, TextInput, TextArea, DateInput, FormGrid } from '../../components/ui';
import { SourceSearch } from './SourceSearch';
import type { PieceSelection, SourceDelivery, SourceInspCandidate, SourceLine } from './SourceSearch';

// 新建客户售后返修批次（三段式：收件信息 → 选择来源 → 核对本次工件）
// 设计文档 docs/aftersales-rework-batch-redesign-2026-10-16.md v3 §5.3
// 草稿允许按数量收件；来源确认（拆单件）在详情页完成。
// 来源检索（逐件勾选/按合同平铺/分页/按工件定位）由 SourceSearch 组件统一实现，与详情页「添加工件」共用。
// task-26 起：同一交货行含多个序列号时按「件」分别勾选 —— 勾 1 件只提交 1 件，不再整行选择。

type DraftLine = {
  uid: string;
  /** 逐件稳定标识：同一交货行多序列号时按件区分（旧响应无 pieces 时等于 line.id）。 */
  piece_key: string;
  /** 真实交货明细行 id（提交给后端的 original_delivery_line_id）。 */
  source_line_id: string;
  original_delivery_id: string;
  /** 逐件序列号；旧响应按整行选择时为 null。 */
  serial_number: string | null;
  /** true = 旧响应无 pieces，按整行选择（数量可编辑、提交不带 serial_number）。 */
  legacy_line: boolean;
  original_inspection_id: string;
  inspMatch: 'unique' | 'multiple' | 'weak' | 'none';
  inspCandidates: SourceInspCandidate[];
  inspMessage?: string | null;
  item_id?: string | null;
  asset_id?: string | null;
  label: string;
  delivery_number: string;
  contract_number: string;
  quantity: number;
  problem: string;
};

// 原检验展示：唯一匹配显示 QC 编号；多候选/弱候选由用户显式下拉；无候选提示由质检确认模板。
function inspectionText(p: DraftLine): string {
  if (p.inspMatch === 'unique') return p.original_inspection_id ? (p.inspCandidates[0]?.number || p.original_inspection_id) : '—';
  return '—';
}

const today = () => new Date().toISOString().slice(0, 10);

export function ReworkBatchEditor({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [step, setStep] = useState(1);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [info, setInfo] = useState({
    customer_repair_number: '', receipt_date: today(), reason: '', expected_completion_date: '', notes: '',
  });
  const setInfoField = (k: string, v: string) => setInfo((s) => ({ ...s, [k]: v }));

  const [picked, setPicked] = useState<DraftLine[]>([]);
  // 已选摘要：跨搜索/分页/合同筛选/选择方式保持（按 piece_key 逐件记录），可展开查看并逐件移除。
  const [showPicked, setShowPicked] = useState(false);

  const togglePiece = (d: SourceDelivery, line: SourceLine, piece: PieceSelection, checked: boolean) => {
    setPicked((cur) => {
      const exists = cur.some((p) => p.piece_key === piece.piece_key);
      if (!checked) return cur.filter((p) => p.piece_key !== piece.piece_key);
      if (exists) return cur;
      const insp = piece.original_inspection;
      return [...cur, {
        uid: piece.piece_key,
        piece_key: piece.piece_key,
        source_line_id: line.id,
        original_delivery_id: d.id,
        serial_number: piece.serial_number,
        legacy_line: piece.legacy_line,
        original_inspection_id: insp.match === 'unique' ? (insp.resolved_id || '') : '',
        inspMatch: insp.match,
        inspCandidates: insp.candidates || [],
        inspMessage: insp.message || null,
        item_id: line.item_id || null,
        asset_id: piece.asset_id || line.asset_id || null,
        label: piece.serial_number || line.customer_serial || line.internal_code || line.serial_numbers || line.description_es || '工件',
        delivery_number: d.number,
        contract_number: d.contract_number || '',
        quantity: 1,
        problem: '',
      }];
    });
  };

  const submit = async () => {
    setErr('');
    if (!customerId) { setErr('请先选择客户'); setStep(1); return; }
    if (!info.reason.trim()) { setErr('返工原因为必填'); setStep(1); return; }
    const needPick = picked.find((p) => (p.inspMatch === 'multiple' || p.inspMatch === 'weak') && !p.original_inspection_id);
    if (needPick) {
      setErr(needPick.inspMatch === 'weak'
        ? `工件「${needPick.label}」仅有「工件+合同」弱候选项，请在第三步确认对应所选原交货的原检验单（禁止自动绑定）`
        : `工件「${needPick.label}」存在多个候选原检验，请在第三步显式选择原检验单`);
      setStep(3); return;
    }
    setBusy(true);
    try {
      await api.post('/api/rework-batches', {
        customer_id: customerId,
        customer_repair_number: info.customer_repair_number.trim() || undefined,
        receipt_date: info.receipt_date,
        reason: info.reason.trim(),
        expected_completion_date: info.expected_completion_date || undefined,
        notes: info.notes.trim() || undefined,
        source_system: 'web-console',
        source_id: `RB/${customerId}/${Date.now()}`,
        items: picked.map((p) => ({
          asset_id: p.asset_id || undefined,
          item_id: p.item_id || undefined,
          // 逐件选择固定 1 件；旧响应整行选择保留可编辑数量。
          quantity: p.legacy_line ? p.quantity : 1,
          original_delivery_id: p.original_delivery_id,
          original_delivery_line_id: p.source_line_id,
          // 逐件提交序列号，后端据此把该交货行收窄到这一件并单独定位原检验。
          serial_number: p.legacy_line ? undefined : (p.serial_number || undefined),
          original_inspection_id: p.original_inspection_id || undefined,
          problem: p.problem.trim() || undefined,
        })),
      });
      onDone();
    } catch (e: any) {
      setErr(e?.message || '创建失败');
    } finally {
      setBusy(false);
    }
  };

  // 已选摘要计数：交货单数 / 合同数（跨搜索/分页/合同筛选累计，按 delivery_line_id 记录）。
  const selectedDeliveryCount = new Set(picked.map((p) => p.original_delivery_id)).size;
  const selectedContractCount = new Set(picked.map((p) => p.contract_number).filter(Boolean)).size;

  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="panel-head">
        <div>
          <h2>新建客户售后返修批次</h2>
          <div className="desc">
            收件信息 → 选择来源（该客户历史交货单）→ 核对本次工件。草稿可按数量收件；来源确认时拆为单件并建立内部工件身份。
          </div>
        </div>
        <div className="spacer" />
        <div className="muted" style={{ fontSize: 12 }}>第 {step} / 3 步</div>
      </div>
      {err ? <div className="notice bad">{err}</div> : null}

      {step === 1 ? (
        <FormGrid>
          <Field label="客户 *" required>
            <SearchSelect<Customer>
              endpoint="/api/customers"
              value={customerId || null}
              label={customerLabel}
              optionTitle={(c) => c.legal_name}
              optionMeta={(c) => c.code}
              placeholder="输入编码 / 名称检索…"
              onChange={(c) => {
                const nextId = c?.id || '';
                // 换客户后已选来源不再属于本批次客户，清空避免把别家交货行带进提交。
                if (nextId !== customerId) setPicked([]);
                setCustomerId(nextId);
                setCustomerLabel(c?.legal_name || '');
              }}
            />
          </Field>
          <Field label="客户返修单号">
            <TextInput value={info.customer_repair_number} onChange={(e) => setInfoField('customer_repair_number', e.target.value)} placeholder="客户侧返修单号（可选）" />
          </Field>
          <Field label="到货日期">
            <DateInput value={info.receipt_date} onChange={(e) => setInfoField('receipt_date', e.target.value)} />
          </Field>
          <Field label="预计完成日期">
            <DateInput value={info.expected_completion_date} onChange={(e) => setInfoField('expected_completion_date', e.target.value)} />
          </Field>
          <Field label="返工原因 *" required span>
            <TextArea value={info.reason} onChange={(e) => setInfoField('reason', e.target.value)} placeholder="本批次统一的返工原因…" />
          </Field>
          <Field label="备注" span>
            <TextArea value={info.notes} onChange={(e) => setInfoField('notes', e.target.value)} />
          </Field>
        </FormGrid>
      ) : null}

      {step === 2 ? (
        <div>
          {!customerId ? <div className="notice">请先在第一步选择客户，再检索该客户历史交货。</div> : (
            <>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                支持按交货单号 / 合同号 / 序列号 / 工件编码搜索、合同筛选与分页；也可切换「按销售合同选择」平铺该合同下工件。
                同一交货行含多个序列号时按件逐行勾选（勾 1 件只提交 1 件）；已选工件跨搜索/分页/合同筛选/选择方式保持，
                可一次从不同合同、不同交货单勾选多件后创建同一批次；未选来源也可直接保存草稿。
              </div>

              {/* 固定已选摘要：跨搜索/分页/合同筛选/选择方式保持（按 piece_key 逐件去重） */}
              <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface, #fff)', padding: '6px 0' }}>
                <div className="panel" style={{ margin: 0, padding: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="strong">已选工件 {picked.length} 件</span>
                    <span className="muted">
                      （逐件计数 · 来自 {selectedDeliveryCount} 张交货单 / {selectedContractCount} 个合同）
                    </span>
                    <div className="spacer" />
                    <button className="btn" onClick={() => setShowPicked((v) => !v)} disabled={picked.length === 0}>
                      {showPicked ? '收起已选' : '查看/移除已选'}
                    </button>
                  </div>
                  {picked.length === 0 ? (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      尚未选择工件：在下方检索结果中勾选即可，选择不会因搜索/翻页/切换合同而丢失。
                    </div>
                  ) : null}
                  {showPicked && picked.length > 0 ? (
                    <table className="grid" style={{ marginTop: 6 }}>
                      <thead>
                        <tr><th>工件</th><th>原交货单</th><th>原合同</th><th>原检验</th><th></th></tr>
                      </thead>
                      <tbody>
                        {picked.map((p, i) => (
                          <tr key={p.uid}>
                            <td className="muted">{p.label}</td>
                            <td className="muted">{p.delivery_number || '—'}</td>
                            <td className="muted">{p.contract_number || '—'}</td>
                            <td>
                              {p.inspMatch === 'unique' ? (
                                <span className="muted">已关联 {inspectionText(p)}</span>
                              ) : p.inspMatch === 'multiple' || p.inspMatch === 'weak' ? (
                                <select
                                  className="sel"
                                  value={p.original_inspection_id}
                                  onChange={(e) => setPicked((cur) => cur.map((x, j) => j === i ? { ...x, original_inspection_id: e.target.value } : x))}
                                  aria-label={`选择原检验单（${p.label}）`}
                                >
                                  <option value="">请选择原检验单…</option>
                                  {p.inspCandidates.map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.number}{c.result ? ` · ${c.result}` : ''}{c.inspection_date ? ` · ${c.inspection_date}` : ''}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span style={{ color: 'var(--bad)' }}>未定位原检验 · 由质检确认模板</span>
                              )}
                            </td>
                            <td>
                              <button className="btn" onClick={() => setPicked((cur) => cur.filter((_, j) => j !== i))}>移除</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              </div>

              <SourceSearch
                customerId={customerId}
                selectedKeys={picked.map((p) => p.piece_key)}
                onTogglePiece={togglePiece}
              />
            </>
          )}
        </div>
      ) : null}

      {step === 3 ? (
        <div>
          {picked.length === 0 ? (
            <div className="notice">未选择来源工件。可直接保存为草稿，稍后在详情页追加工件。</div>
          ) : (
            <table className="grid">
              <thead>
                <tr>
                  <th>本次工件</th><th>原交货单</th><th>原合同</th><th style={{ width: 90 }}>数量</th>
                  <th>本次问题</th><th style={{ width: 220 }}>原检验（来源快照）</th><th></th>
                </tr>
              </thead>
              <tbody>
                {picked.map((p, i) => (
                  <tr key={p.uid}>
                    <td className="muted">{p.label}</td>
                    <td className="muted">{p.delivery_number || '—'}</td>
                    <td className="muted">{p.contract_number || '—'}</td>
                    <td>
                      {p.legacy_line ? (
                        <TextInput
                          type="number"
                          min={1}
                          value={String(p.quantity)}
                          onChange={(e) => setPicked((cur) => cur.map((x, j) => j === i ? { ...x, quantity: Math.max(1, Number(e.target.value) || 1) } : x))}
                        />
                      ) : (
                        <span className="muted" title="逐件选择固定 1 件（原交货行多序列号已按件拆分）">1（单件）</span>
                      )}
                    </td>
                    <td>
                      <TextInput
                        value={p.problem}
                        placeholder="本件问题描述（可选）"
                        onChange={(e) => setPicked((cur) => cur.map((x, j) => j === i ? { ...x, problem: e.target.value } : x))}
                      />
                    </td>
                    <td>
                      {p.inspMatch === 'unique' ? (
                        <span className="muted">自动关联 {inspectionText(p)}</span>
                      ) : p.inspMatch === 'multiple' || p.inspMatch === 'weak' ? (
                        <select
                          className="txt"
                          value={p.original_inspection_id}
                          onChange={(e) => setPicked((cur) => cur.map((x, j) => j === i ? { ...x, original_inspection_id: e.target.value } : x))}
                          aria-label={`选择原检验单（${p.label}）`}
                        >
                          <option value="">请选择原检验单…</option>
                          {p.inspCandidates.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.number}{c.result ? ` · ${c.result}` : ''}{c.inspection_date ? ` · ${c.inspection_date}` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ color: 'var(--bad)' }}>{p.inspMessage || '未定位原检验；将由质检人员确认模板并记录原因，空快照不得放行'}</span>
                      )}
                    </td>
                    <td><button className="btn" onClick={() => setPicked((cur) => cur.filter((_, j) => j !== i))}>移除</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      <div className="detail-actions" style={{ marginTop: 12 }}>
        {step > 1 ? <button className="btn" onClick={() => setStep((s) => s - 1)} disabled={busy}>上一步</button> : null}
        {step < 3 ? (
          <button
            className="btn primary"
            onClick={() => {
              setErr('');
              if (step === 1 && (!customerId || !info.reason.trim())) { setErr('客户与返工原因为必填'); return; }
              setStep((s) => s + 1);
            }}
          >
            下一步
          </button>
        ) : (
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建返修批次'}</button>
        )}
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}
